import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const inputVariants = cva(
  'h-12 w-full rounded-md border border-border-subtle bg-surface-input px-4 text-base font-sans font-normal leading-[1.5] text-fg-primary placeholder:text-fg-secondary placeholder:opacity-75 [border-color:var(--input-border-color,var(--color-border-subtle))] [background-color:var(--input-bg,var(--color-surface-input))] [color:var(--input-fg,var(--color-fg-primary))] placeholder:[color:var(--input-placeholder,var(--color-fg-secondary))] transition-[border-color,box-shadow] duration-150 ease-in-out focus-visible:[border-color:var(--input-focus-border,var(--color-ring-default))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:[box-shadow:inset_0_0_0_2px_var(--input-focus-ring,var(--color-ring-default))] disabled:cursor-not-allowed disabled:opacity-60',
  {
    variants: {
      state: {
        default: '',
        error:
          'border-status-danger focus-visible:border-status-danger focus-visible:ring-[var(--color-ring-danger)]',
      },
      inputSize: {
        default: 'h-12 px-4 text-base',
        sm: 'h-9 text-sm',
        large: 'h-14 px-5 text-base',
      },
      context: {
        default: '',
        modal:
          '[--input-border-color:var(--color-modal-border)] bg-[var(--surface-tint-inverse)] text-[var(--color-modal-fg)] placeholder:text-[var(--color-modal-fg)] focus-visible:border-[var(--color-modal-fg)] focus-visible:!ring-0',
      },
    },
    defaultVariants: {
      state: 'default',
      inputSize: 'default',
      context: 'default',
    },
  }
);

type InputProps = React.ComponentProps<'input'> &
  VariantProps<typeof inputVariants>;

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = 'text', state, inputSize, context, ...props }, ref) => {
    const shouldUseCompactHorizontalPadding =
      type === 'text' ||
      type === 'number' ||
      type === 'email' ||
      type === 'password';

    return (
      <input
        data-slot="input"
        type={type}
        className={cn(
          inputVariants({ state, inputSize, context }),
          shouldUseCompactHorizontalPadding && 'pl-3 pr-2',
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);

Input.displayName = 'Input';

export { Input };
